import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageHeaderContextType {
  breadcrumb: BreadcrumbItem[] | null;
  setBreadcrumb: (items: BreadcrumbItem[] | null) => void;
}

const PageHeaderContext = createContext<PageHeaderContextType | undefined>(undefined);

interface PageHeaderProviderProps {
  children: ReactNode;
}

/**
 * Lets a page override the top app bar's title with a breadcrumb trail
 * (e.g. "Ticket Management / <ticket title>") without AppShell needing to
 * know about that page's data. Pages must clear the breadcrumb on unmount
 * so navigating elsewhere falls back to the default page title.
 */
export const PageHeaderProvider: React.FC<PageHeaderProviderProps> = ({ children }) => {
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[] | null>(null);

  const value = useMemo(() => ({ breadcrumb, setBreadcrumb }), [breadcrumb]);

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export const usePageHeaderContext = (): PageHeaderContextType => {
  const context = useContext(PageHeaderContext);
  if (context === undefined) {
    throw new Error('usePageHeaderContext must be used within a PageHeaderProvider');
  }
  return context;
};

/**
 * Sets the top app bar breadcrumb for the lifetime of the calling component,
 * clearing it automatically on unmount. Pass `null` while data is still
 * loading to keep the default page title until there's something to show.
 */
export const useHeaderBreadcrumb = (items: BreadcrumbItem[] | null): void => {
  const { setBreadcrumb } = usePageHeaderContext();

  React.useEffect(() => {
    setBreadcrumb(items);
    return () => setBreadcrumb(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items)]);
};
